import React, { useEffect, useRef, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useLocalContent } from "~/sdks/LocalContent/context";

import { CaretRightIcon } from "@phosphor-icons/react";

import { DEFAULT_RADIUS } from "~/sdks/LocalContent/constants";

import { getIdFromMarker } from "~/sdks/LocalContent/utils/getIdFromMarker";

import { TransitRoutes } from "~/sdks/LocalContent/components/TransitRoutes";

import type { POISubcategory } from "~/sdks/LocalContent/types";
import type { POI, School } from "@local-logic/client";
import type { MarkerValue } from "../types";

import * as styles from "./styles";

export function List({
  pois,
  showPois = true,
  onSelect,
}: {
  pois: MarkerValue[];
  showPois?: boolean;
  onSelect?: (poi: MarkerValue, category: POISubcategory) => void;
}) {
  const { t } = useTranslation(["localContent", "common"]);
  const {
    activeMarker,
    markersData,
    distanceUnit,
    selectedPoiSubcategory,
    handleMarkerClick,
  } = useLocalContent();
  const activeItemRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeMarker]);

  const isUsingLargerRadius = useMemo(() => {
    if (!markersData || Object.keys(markersData).length === 0) {
      return false;
    }

    const farMarker = Object.values(markersData)
      .flat()
      .find(
        (marker) =>
          typeof marker?.proximity?.linear_distance === "number" &&
          marker.proximity.linear_distance > DEFAULT_RADIUS
      );

    return typeof farMarker !== "undefined";
  }, [markersData]);

  const handleSelectMarkerOnMap = (poi: MarkerValue) => () => {
    const markerId = getIdFromMarker(poi.lat, poi.lng, poi.name);
    handleMarkerClick({
      id: markerId,
      latitude: poi.lat,
      longitude: poi.lng,
      markers: [
        {
          id: markerId,
          latitude: poi.lat,
          longitude: poi.lng,
          name: poi.name,
        },
      ],
    });

    if (typeof onSelect !== "undefined" && selectedPoiSubcategory !== null) {
      onSelect(poi, selectedPoiSubcategory);
    }
  };

  if (pois.length === 0) {
    return null;
  }

  return (
    <div className={styles.container} data-testid="score-poi-list-content">
      <p className={styles.title} data-testid="score-poi-list-title">
        {!isUsingLargerRadius &&
          t("poiList.searchRadius", {
            radius:
              distanceUnit === "imperial"
                ? ((DEFAULT_RADIUS / 1000) * 0.621371).toFixed(1)
                : (DEFAULT_RADIUS / 1000).toFixed(1),
            unit: t(
              `common:distance.${distanceUnit === "imperial" ? "mile" : "kilometer"}`
            ),
          })}
      </p>

      {showPois && (
        <ul className={styles.list}>
          {pois.map((poi) => {
            const isActive =
              activeMarker?.id === getIdFromMarker(poi.lat, poi.lng, poi.name);

            return (
              <li
                ref={isActive ? activeItemRef : null}
                className={styles.item}
                key={`${poi.lat}-${poi.lng}-${poi.name}`}
                data-is-active={isActive}
              >
                <button
                  onClick={handleSelectMarkerOnMap(poi)}
                  className={styles.button}
                >
                  <span className={styles.poi}>
                    <span
                      className={styles.name}
                      data-testid="score-poi-list-name"
                    >
                      {poi.name}
                    </span>
                    <CaretRightIcon weight="bold" className={styles.icon} />
                  </span>

                  {typeof (poi as School)?.school_attributes?.types !==
                    "undefined" && (
                    <div className={styles.schoolTypes}>
                      {(poi as School)?.school_attributes?.types?.map(
                        (schoolType) => (
                          <span
                            key={`score-poi-list-school-type-${poi.lat}-${poi.lng}-${poi.name}`}
                            className={styles.badge}
                          >
                            {schoolType}
                          </span>
                        )
                      )}
                    </div>
                  )}

                  <TransitRoutes
                    keyPrefix={`poi-transit-routes-${poi.lat}-${poi.lng}`}
                    routes={(poi as POI).routes}
                    useLongName={selectedPoiSubcategory === "train_stations"}
                    className={styles.routes}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
